import { useEffect, useState } from "react";
import { DualCameraDisplay } from "../components/DualCameraView";
import { toast } from "sonner";
import emotionDetectionService from "@/utils/emotionDetectionService";

export default function VideoRecording() {
  const [isLoading, setIsLoading] = useState(true);
  const [hasPermissions, setHasPermissions] = useState(false);
  const [isLoadingModels, setIsLoadingModels] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Create a flag to track whether the component is mounted
    let isMounted = true;
    
    // Function to load dependencies
    const loadDependencies = async () => {
      try {
        // Check if browser supports getUserMedia
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          console.log("Browser doesn't support camera access");
          toast.error("Your browser doesn't support camera access. Please try a different browser.");
          return;
        }

        // Check camera permissions with less strict requirements first
        try {
          console.log("Testing camera access...");
          const stream = await navigator.mediaDevices.getUserMedia({ 
            video: true,
            audio: true
          });
          
          // Keep track of whether permissions were granted
          if (isMounted) {
            setHasPermissions(true);
          }
          
          // Stop the test stream immediately
          stream.getTracks().forEach(track => track.stop());
          
          console.log("Camera and microphone permissions granted");
        } catch (err) {
          console.error("Media access error:", err);
          if (isMounted) {
            toast.error("Camera access is required. Please allow camera access and refresh the page.");
          }
          return;
        }
        
        // Start loading models in parallel to speed up initialization
        if (isMounted) {
          setIsLoadingModels(true);
        }
        
        // Load emotion detection models with better error handling
        try {
          console.log("Starting emotion detection model loading...");
          const success = await emotionDetectionService.loadModels();
          
          console.log("Face models loaded:", success);
          if (isMounted) {
            setIsLoadingModels(false);
            toast.success("Emotion detection ready");
          }
        } catch (error) {
          console.error("Error loading emotion detection models:", error);
          if (isMounted) {
            setIsLoadingModels(false);
            toast.warning("Emotion detection may have limited functionality");
          }
        }
        
        // Setup is complete
        if (isMounted) {
          setIsLoading(false);
          setIsInitialized(true);
        }
      } catch (error) {
        console.error("Error initializing page:", error);
        if (isMounted) {
          toast.error("Error initializing. Please refresh the page.");
          setIsLoading(false);
        }
      }
    };

    // Start loading dependencies
    loadDependencies();
    
    // Cleanup function
    return () => {
      isMounted = false;
      console.log("VideoRecording unmounting, cleaning up");
    };
  }, []);

  const getLoadingMessage = () => {
    if (isLoading) return "Initializing camera...";
    if (isLoadingModels) return "Loading emotion detection models...";
    return "Initializing...";
  };

  const handleRetry = () => {
    setIsLoading(true);
    setIsLoadingModels(true);
    setIsInitialized(false);
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-black">
      <main className="mt-0 p-0">
        {(isLoading || isLoadingModels) ? (
          <div className="flex flex-col justify-center items-center h-[80vh]">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-700 mb-4"></div>
            <p className="text-white">{getLoadingMessage()}</p>
          </div>
        ) : !hasPermissions ? (
          <div className="flex flex-col justify-center items-center h-[80vh]">
            <p className="text-white text-xl mb-4">Camera permission required</p>
            <button 
              className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded"
              onClick={handleRetry}
            >
              Retry Camera Access
            </button>
          </div>
        ) : !isInitialized ? (
          <div className="flex flex-col justify-center items-center h-[80vh]">
            <p className="text-white text-xl mb-4">Camera initialization failed</p>
            <button 
              className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded"
              onClick={handleRetry}
            >
              Retry Initialization
            </button>
          </div>
        ) : (
          <DualCameraDisplay isRecordingScreen={true} />
        )}
      </main>
    </div>
  );
}
